import type { RenderContext } from "./canvas-surface";
import { isGradient, stopColor } from "../native/gradient";

/** Paint coordinates are local pixels; the active object transform is applied when painting. */
export function canvasPaint(
  ctx: RenderContext,
  color: string,
  value: unknown,
  width: number,
  height: number,
  x = -width / 2,
  y = -height / 2,
): string | CanvasGradient {
  if (!isGradient(value)) return color;
  const cx = x + width / 2,
    cy = y + height / 2;
  let paint: CanvasGradient;
  const points = value.coordinates;
  if (points) {
    const args = [
      x + points.x1 * width,
      y + points.y1 * height,
      x + points.x2 * width,
      y + points.y2 * height,
    ] as const;
    paint =
      value.type === "linear"
        ? ctx.createLinearGradient(...args)
        : ctx.createRadialGradient(
            args[0],
            args[1],
            points.r1 * Math.min(width, height),
            args[2],
            args[3],
            points.r2 * Math.min(width, height),
          );
  } else if (value.type === "radial")
    paint = ctx.createRadialGradient(
      cx,
      cy,
      0,
      cx,
      cy,
      Math.max(0.001, Math.min(width, height) / 2),
    );
  else {
    const a = (value.angle * Math.PI) / 180,
      dx = Math.cos(a),
      dy = Math.sin(a),
      extent = (Math.abs(dx) * width + Math.abs(dy) * height) / 2;
    paint = ctx.createLinearGradient(
      cx - dx * extent,
      cy - dy * extent,
      cx + dx * extent,
      cy + dy * extent,
    );
  }
  for (const stop of value.stops)
    paint.addColorStop(stop.offset, stopColor(stop));
  return paint;
}
